import * as React from 'react';
import { RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getAdminUserStats, getLoginLogs } from '@/api/client';

type UserStat = {
  id: number;
  username: string;
  display_name: string;
  role: string;
  status: string;
  created_at: string;
  last_login: string | null;
  login_count: number;
  fail_count: number;
  accounts: { douyin: number; xiaohongshu: number; wechat: number };
  accounts_total: number;
  library: { douyin: number; xiaohongshu: number; wechat: number };
  library_total: number;
  transcribed: number;
  usage: { checks: number; extracts: number; transcribe_seconds: number; rewrites: number };
  has_persona: boolean;
  has_tikhub: boolean;
  has_llm: boolean;
  has_asr: boolean;
};

function formatDur(s: number): string {
  if (!s) return '0';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}分`;
  return `${(m / 60).toFixed(1)}时`;
}

function fmtTime(t: string | null | undefined): string {
  if (!t) return '—';
  const d = new Date(t);
  if (isNaN(d.getTime())) return t;
  return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function NumBox({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className={`text-2xl font-bold ${accent || ''}`}>{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

export function UserStatsTab() {
  const [summary, setSummary] = React.useState<any>(null);
  const [users, setUsers] = React.useState<UserStat[]>([]);
  const [month, setMonth] = React.useState('');
  const [loading, setLoading] = React.useState(true);

  // 登录日志弹窗
  const [logUser, setLogUser] = React.useState<string | null>(null);
  const [logs, setLogs] = React.useState<any[]>([]);
  const [logsLoading, setLogsLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminUserStats();
      setSummary(data.summary);
      setUsers(data.users || []);
      setMonth(data.month);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openLogs = React.useCallback(async (username: string) => {
    setLogUser(username);
    setLogsLoading(true);
    setLogs([]);
    try {
      const data = await getLoginLogs(username);
      setLogs(data.logs || []);
    } catch { setLogs([]); } finally {
      setLogsLoading(false);
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* 总览卡片 */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">用户数据看板</CardTitle>
            <CardDescription className="mt-1 text-xs">全平台用户使用情况（{month}）· 仅管理员可见</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} title="刷新">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        {loading || !summary ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
          </div>
        ) : (
          <>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <NumBox label="总用户" value={summary.users} />
              <NumBox label="活跃用户（角色正常）" value={`${summary.active_users} / ${summary.users}`} accent="text-emerald-500" />
              <NumBox label="本月新增" value={summary.new_this_month} accent="text-sky-500" />
              <NumBox label="7天内登录" value={summary.login_this_week} accent="text-amber-500" />
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <NumBox label="监控账号总数" value={summary.accounts_total} />
              <NumBox label="内容条目总数" value={summary.library_total} />
              <NumBox label="已转录" value={summary.transcribed_total} accent="text-purple-500" />
              <NumBox label="本月提取次数" value={summary.extracts_this_month} />
            </div>
          </>
        )}
      </div>

      {/* 用户明细表 */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>用户明细</CardTitle>
          <CardDescription>{users.length} 个用户</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="px-3 py-2.5 font-medium">用户</th>
                  <th className="px-3 py-2.5 font-medium">注册</th>
                  <th className="px-3 py-2.5 font-medium">最近登录</th>
                  <th className="px-3 py-2.5 font-medium text-right">登录/失败</th>
                  <th className="px-3 py-2.5 font-medium text-center">监控账号</th>
                  <th className="px-3 py-2.5 font-medium text-center">内容库</th>
                  <th className="px-3 py-2.5 font-medium text-center">已转录</th>
                  <th className="px-3 py-2.5 font-medium text-right">本月用量</th>
                  <th className="px-3 py-2.5 font-medium text-center">配置</th>
                  <th className="px-3 py-2.5 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}><td className="p-4" colSpan={10}><Skeleton className="h-8 w-full" /></td></tr>
                  ))
                ) : users.length === 0 ? (
                  <tr><td className="px-4 py-8 text-center text-muted-foreground" colSpan={10}>暂无用户数据</td></tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="border-b last:border-0 align-top">
                      <td className="px-3 py-3">
                        <div className="font-medium">{u.username}</div>
                        <div className="text-xs text-muted-foreground">
                          {u.display_name || '—'}
                          {u.role === 'admin' && <Badge variant="secondary" className="ml-1">管理员</Badge>}
                          {u.status === 'disabled' && <Badge variant="destructive" className="ml-1">禁用</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">{fmtTime(u.created_at)}</td>
                      <td className="px-3 py-3 text-xs">{fmtTime(u.last_login)}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-emerald-500">{u.login_count}</span>
                        <span className="text-muted-foreground"> / </span>
                        <span className={u.fail_count ? 'text-red-500' : 'text-muted-foreground'}>{u.fail_count}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {u.accounts_total > 0 ? (
                          <div>
                            <span className="font-medium">{u.accounts_total}</span>
                            <div className="text-xs text-muted-foreground">
                              {u.accounts.douyin > 0 && <span className="mr-1">抖{u.accounts.douyin}</span>}
                              {u.accounts.xiaohongshu > 0 && <span className="mr-1">红{u.accounts.xiaohongshu}</span>}
                              {u.accounts.wechat > 0 && <span className="mr-1">视{u.accounts.wechat}</span>}
                            </div>
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {u.library_total > 0 ? (
                          <div>
                            <span className="font-medium">{u.library_total}</span>
                            <div className="text-xs text-muted-foreground">
                              {u.library.douyin > 0 && <span className="mr-1">抖{u.library.douyin}</span>}
                              {u.library.xiaohongshu > 0 && <span className="mr-1">红{u.library.xiaohongshu}</span>}
                              {u.library.wechat > 0 && <span className="mr-1">视{u.library.wechat}</span>}
                            </div>
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-3 text-center text-purple-500 font-medium">
                        {u.transcribed > 0 ? u.transcribed : <span className="text-muted-foreground font-normal">—</span>}
                      </td>
                      <td className="px-3 py-3 text-right text-xs">
                        <div>检 {u.usage.checks} · 提 {u.usage.extracts}</div>
                        <div className="text-muted-foreground">· 转 {formatDur(u.usage.transcribe_seconds)} · 改 {u.usage.rewrites}</div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex flex-wrap justify-center gap-1 text-xs">
                          {u.has_persona && <Badge variant="outline" className="text-[10px]">人设</Badge>}
                          {u.has_tikhub && <Badge variant="outline" className="text-[10px]">TikHub</Badge>}
                          {u.has_llm && <Badge variant="outline" className="text-[10px]">LLM</Badge>}
                          {u.has_asr && <Badge variant="outline" className="text-[10px]">ASR</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openLogs(u.username)}>
                          登录日志
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 登录日志弹窗 */}
      {logUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setLogUser(null)}>
          <div className="w-full max-w-lg rounded-lg border bg-background shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <div className="font-medium">登录日志 · {logUser}</div>
                <div className="text-xs text-muted-foreground">最近 50 条</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setLogUser(null)}>关闭</Button>
            </div>
            <div className="max-h-80 overflow-y-auto p-2">
              {logsLoading ? (
                <div className="p-4 text-center text-sm text-muted-foreground">加载中…</div>
              ) : logs.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">暂无记录</div>
              ) : (
                logs.map((l) => (
                  <div key={l.id} className="flex items-start gap-3 border-b p-2 last:border-0">
                    <Badge variant={l.action === 'login' ? 'success' : 'destructive'} className="mt-0.5 w-12 shrink-0 justify-center text-[10px]">
                      {l.action === 'login' ? '成功' : '失败'}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-muted-foreground">{fmtTime(l.created_at)} · {l.ip_address || '—'}</div>
                      {l.detail && <div className="mt-0.5 text-xs text-muted-foreground">{l.detail}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
